/**
 * SchoolVerse strict client-side input boundary.
 *
 * This is a global input boundary for the existing application. It works
 * without requiring every form to be rewritten and covers typing, paste,
 * drag/drop and programmatic changes that surface through input/change.
 *
 * Important: this is a UX/input boundary, not a security boundary. Server-side
 * schemas and validation remain authoritative and are hardened separately.
 */

const LIMITS = {
  text: 500,
  textarea: 5000,
  email: 254,
  tel: 30,
  password: 128,
  url: 2048,
  search: 200,
  number: 20,
  date: 10,
  month: 7,
  week: 8,
  time: 8,
  'datetime-local': 16,
};

// These are intentionally non-global because beforeinput uses RegExp.test().
const EMAIL_INVALID = /[^A-Za-z0-9.!#$%&'*+\/= ?^_`{|}~@-]/;
const EMAIL_STRIP = /[^A-Za-z0-9.!#$%&'*+\/=^_`{|}~@-]/g;
const PHONE_INVALID = /[^0-9+()\-\s]/;
const PHONE_STRIP = /[^0-9+()\-\s]/g;
const NUMBER_INVALID = /[^0-9.\-]/;
const NUMBER_STRIP = /[^0-9.\-]/g;
const URL_INVALID = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\r\n\t\s]/;
const CONTROL_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
const DATE_INVALID = /[^0-9-]/g;
const MONTH_INVALID = /[^0-9-]/g;
const WEEK_INVALID = /[^0-9W-]/g;
const TIME_INVALID = /[^0-9:]/g;

function inputType(el) {
  return (el.type || 'text').toLowerCase();
}

function maxLengthFor(el) {
  if (el.maxLength > 0) return el.maxLength;
  const type = inputType(el);
  const tag = el.tagName.toLowerCase();
  return LIMITS[type] || (tag === 'textarea' ? LIMITS.textarea : LIMITS.text);
}

function isTextArea(el) {
  return el.tagName.toLowerCase() === 'textarea';
}

function sanitizeNumber(el, value) {
  let next = value.replace(NUMBER_STRIP, '');
  const negativeAllowed = el.min === '' || Number(el.min) < 0;
  if (!negativeAllowed) next = next.replace(/-/g, '');
  // Only one decimal separator and only a leading minus are accepted.
  next = next.replace(/(\..*)\./g, '$1');
  next = next.replace(/(?!^)-/g, '');
  return next;
}

function sanitizeValue(el, value) {
  const type = inputType(el);
  let next = String(value ?? '');

  if (type === 'email') {
    next = next.replace(EMAIL_STRIP, '');
    // An email may be incomplete while the user is typing, but it must never
    // contain multiple @ characters.
    const at = next.indexOf('@');
    if (at !== -1) next = next.slice(0, at + 1) + next.slice(at + 1).replace(/@/g, '');
  } else if (type === 'tel') {
    next = next.replace(PHONE_STRIP, '');
  } else if (type === 'number') {
    next = sanitizeNumber(el, next);
  } else if (type === 'date' || type === 'month' || type === 'week') {
    if (type === 'week') next = next.replace(WEEK_INVALID, '');
    else next = next.replace(DATE_INVALID, '');
  } else if (type === 'time' || type === 'datetime-local') {
    next = next.replace(TIME_INVALID, '');
    if (type === 'datetime-local') next = String(value ?? '').replace(/[^0-9T:\-]/g, '');
  } else if (type === 'url') {
    next = next.replace(CONTROL_CHARS, '').replace(/[\r\n\t\s]/g, '');
  } else if (type !== 'password') {
    // Normal text keeps Unicode, punctuation and spaces. Invisible control
    // characters are not accepted. Newlines are reserved for textareas.
    next = next.replace(CONTROL_CHARS, '');
    if (!isTextArea(el)) next = next.replace(/[\r\n\t]/g, '');
  }

  return next.slice(0, maxLengthFor(el));
}

function setValueAndNotify(el, next) {
  if (el.value === next) return false;
  const start = typeof el.selectionStart === 'number' ? el.selectionStart : next.length;
  const delta = next.length - el.value.length;
  el.value = next;
  const pos = Math.max(0, Math.min(next.length, start + delta));
  try { el.setSelectionRange(pos, pos); } catch (_) {}
  el.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

function isAllowedData(el, data) {
  const type = inputType(el);
  if (type === 'email') return !EMAIL_INVALID.test(data);
  if (type === 'tel') return !PHONE_INVALID.test(data);
  if (type === 'number') return !NUMBER_INVALID.test(data);
  if (type === 'date' || type === 'month') return !/[^0-9-]/.test(data);
  if (type === 'week') return !/[^0-9W-]/.test(data);
  if (type === 'time') return !/[^0-9:]/.test(data);
  if (type === 'datetime-local') return !/[^0-9T:\-]/.test(data);
  if (type === 'url') return !URL_INVALID.test(data);
  if (type !== 'password') {
    return isTextArea(el)
      ? !CONTROL_CHARS.test(data)
      : !/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\r\n\t]/.test(data);
  }
  return true;
}

function beforeInputHandler(event) {
  const el = event.target;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (el.disabled || el.readOnly || event.isComposing) return;

  const data = event.data;
  if (!data) return;

  const max = maxLengthFor(el);
  const start = typeof el.selectionStart === 'number' ? el.selectionStart : el.value.length;
  const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
  if ((el.value.length - (end - start) + data.length) > max) {
    event.preventDefault();
    return;
  }

  if (!isAllowedData(el, data)) event.preventDefault();
}

function inputHandler(event) {
  const el = event.target;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (el.disabled || el.readOnly || event.isComposing) return;
  const next = sanitizeValue(el, el.value);
  if (el.value !== next) setValueAndNotify(el, next);
}

function changeHandler(event) {
  const el = event.target;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (el.disabled || el.readOnly) return;
  const next = sanitizeValue(el, el.value);
  if (el.value !== next) setValueAndNotify(el, next);
}

function pasteHandler(event) {
  const el = event.target;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (el.disabled || el.readOnly) return;
  const text = event.clipboardData?.getData('text');
  if (text == null) return;
  // The input event is the final boundary, but blocking an entirely invalid
  // paste avoids the browser briefly inserting disallowed content.
  if (!isAllowedData(el, text) || text.length > maxLengthFor(el)) event.preventDefault();
}

function dropHandler(event) {
  const el = event.target;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (el.disabled || el.readOnly) return;
  const text = event.dataTransfer?.getData('text');
  if (text == null) return;
  if (!isAllowedData(el, text) || text.length > maxLengthFor(el)) event.preventDefault();
}

export function installStrictInputValidation() {
  if (typeof document === 'undefined') return () => {};
  document.addEventListener('beforeinput', beforeInputHandler, true);
  document.addEventListener('input', inputHandler, true);
  document.addEventListener('change', changeHandler, true);
  document.addEventListener('paste', pasteHandler, true);
  document.addEventListener('drop', dropHandler, true);

  return () => {
    document.removeEventListener('beforeinput', beforeInputHandler, true);
    document.removeEventListener('input', inputHandler, true);
    document.removeEventListener('change', changeHandler, true);
    document.removeEventListener('paste', pasteHandler, true);
    document.removeEventListener('drop', dropHandler, true);
  };
}

export default installStrictInputValidation;
